import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import {
    FormsModule,
    ReactiveFormsModule,
    UntypedFormBuilder,
    UntypedFormGroup,
    Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { CommonModule } from '@angular/common';
import { ProjectService } from '../../shared/services/project.service';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'app-edit-admin-dialog',
    templateUrl: './edit-admin-dialog.component.html',
    styleUrl: './edit-admin-dialog.component.scss',
    imports: [
        CommonModule,
        MatFormFieldModule,
        ReactiveFormsModule,
        MatInputModule,
        FormsModule,
        MatButtonModule,
        MatIconModule,
        MatSelectModule,
        MatDialogModule,
        MatSnackBarModule,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditAdminDialogComponent implements OnInit {
    readonly dialogRef = inject(MatDialogRef<EditAdminDialogComponent>);
    readonly data = inject<any>(MAT_DIALOG_DATA);

    adminForm: UntypedFormGroup;
    isLoading: boolean = false;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    constructor(
        private _formBuilder: UntypedFormBuilder,
        private projectService: ProjectService,
        private snackBar: MatSnackBar
    ) {
        this.adminForm = this._formBuilder.group({
            name: ['', Validators.required],
            email: [{ value: '', disabled: true }],
            companyName: [''],
        });
    }

    ngOnInit(): void {
        if (this.data?.admin) {
            this.loadAdminData(this.data.admin);
        }
    }

    private loadAdminData(admin: any): void {
        this.adminForm.patchValue({
            name: admin.name || '',
            email: admin.email || '',
            companyName: admin.company?.name || admin.companyName || '',
        });
    }

    onSubmit(): void {
        if (this.adminForm.invalid || this.isLoading) {
            return;
        }

        this.isLoading = true;

        const formValue = this.adminForm.value;
        const adminId = this.data.admin._id;

        // Update the admin as super admin
        this.projectService
            .updateAdminBySuperAdmin(adminId, formValue)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                next: (response) => {
                    this.snackBar.open('Admin updated successfully', 'Close', {
                        duration: 3000,
                    });
                    this.dialogRef.close(response);
                },
                error: (err) => {
                    console.error('Error updating admin:', err);
                    this.snackBar.open(err.message || 'Error updating admin', 'Close', {
                        duration: 5000,
                    });
                    this.isLoading = false;
                },
            });
    }

    onCancel(): void {
        this.dialogRef.close();
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }
}
